import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Hidden,
  PageBlock,
  Strong,
  Text,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import React, { useState } from 'react';

import {
  GetUserEmailVerifiedDocument,
  SendEmailVerificationLinkDocument,
} from 'src/generated/graphql';

interface VerifyEmailAlertProps {
  width: 'small' | 'medium' | 'large';
}

export const VerifyEmailAlert = ({ width }: VerifyEmailAlertProps) => {
  const showToast = useToast();
  const { data, loading, error } = useQuery(GetUserEmailVerifiedDocument);
  const [sendEmailVerificationLink] = useMutation(
    SendEmailVerificationLinkDocument,
  );
  const [verificationSent, setVerificationSent] = useState(false);

  const loggedInEmail = data?.user?.emailAddress;
  const userIsVerified = data?.user?.emailVerified;

  if (loading) {
    return <Hidden>Loading, but dont show loader</Hidden>;
  }

  if (error || !loggedInEmail) {
    return <Hidden>Error loading user, fail silently</Hidden>;
  }

  if (userIsVerified) {
    return <Hidden>User is already verified</Hidden>;
  }

  const onClick = async () => {
    try {
      await sendEmailVerificationLink();
      setVerificationSent(true);
    } catch {
      showToast({
        tone: 'critical',
        message: 'Something went wrong',
        description:
          'Please try again, and contact us if the problem persists.',
        data: { testid: 'verification-message-error-toast' },
      });
    }
  };

  return (
    <PageBlock width={width}>
      <Box paddingY="xsmall">
        <Alert tone="caution">
          {verificationSent ? (
            <Text>
              Check your email. We sent a verification link to{' '}
              <Strong>{loggedInEmail}.</Strong> This link expires in 24 hours.
            </Text>
          ) : (
            <Text>
              We need to verify your email <Strong>{loggedInEmail}</Strong> to
              keep your account secure.{' '}
              <TextLinkButton hitArea="large" onClick={onClick}>
                Send verification link.
              </TextLinkButton>
            </Text>
          )}
        </Alert>
      </Box>
    </PageBlock>
  );
};
